import './style.css';
import { watchAuth, refreshAccountFlags, requiresEmailVerification } from './lib/auth.js';
import { startSessionGuard, stopSessionGuard } from './lib/session.js';
import { route, startRouter, navigate } from './router.js';
import { renderLogin } from './views/login.js';
import { renderVerifyEmail } from './views/verifyEmail.js';
import { renderHome } from './views/home.js';
import { renderCourse } from './views/course.js';
import { renderStudio } from './views/studio.js';
import { renderSettings } from './views/settings.js';
import { renderMarketplace } from './views/marketplace.js';
import { renderAdmin } from './views/admin.js';
import { renderWallet } from './views/wallet.js';
import { renderSchool } from './views/school.js';
import { renderSchoolStart } from './views/schoolStart.js';
import { renderScores } from './views/scores.js';
import { renderSchoolStudentScores } from './views/schoolStudentScores.js';
import { renderSchoolScores } from './views/schoolScores.js';
import { renderTeaching } from './views/teaching.js';
import { renderLanding } from './views/marketing/landing.js';
import { renderPricing } from './views/marketing/pricing.js';
import { renderSchools } from './views/marketing/schools.js';
import { renderAbout } from './views/marketing/about.js';
import { renderContact } from './views/marketing/contact.js';
import { renderTerms } from './views/marketing/terms.js';
import { renderPrivacy } from './views/marketing/privacy.js';
import { renderRefund } from './views/marketing/refund.js';

const app = document.getElementById('app');
let authReady = false;
let signedIn = false;

// Routes that never require sign-in — the public marketing site. '/' is
// intentionally handled separately below since it shows different content
// depending on auth state rather than being a fixed public page.
const PUBLIC_PATHS = new Set(['/', '/login', '/verify-email', '/school/start', '/pricing', '/about', '/contact', '/terms', '/privacy', '/refund']);

function marketing(renderFn) {
  return async () => {
    if (!authReady) {
      app.innerHTML = `<div class="center-screen">Loading…</div>`;
      return;
    }
    renderFn(app, signedIn);
  };
}

// Apply saved theme before first paint.
const savedTheme = localStorage.getItem('ll-theme');
if (savedTheme) document.documentElement.setAttribute('data-theme', savedTheme);

function guard(renderFn) {
  return async (params) => {
    if (!authReady) {
      app.innerHTML = `<div class="center-screen">Loading…</div>`;
      return;
    }
    if (!signedIn) {
      navigate('/login');
      return;
    }
    return await renderFn(app, params);
  };
}

route('/login', async () => {
  if (signedIn) { navigate('/'); return; }
  if (requiresEmailVerification()) { navigate('/verify-email'); return; }
  renderLogin(app);
});
route('/verify-email', async () => {
  if (signedIn) { navigate('/'); return; }
  renderVerifyEmail(app);
});
// '/' is the public landing page for signed-out visitors and the signed-in
// dashboard once authenticated — no separate "app home" URL needed, and
// every existing navigate('/') call (post-login, sign-out, etc.) keeps
// working exactly as before.
route('/', async (params) => {
  if (!authReady) {
    app.innerHTML = `<div class="center-screen">Loading…</div>`;
    return;
  }
  if (requiresEmailVerification()) { navigate('/verify-email'); return; }
  if (!signedIn) { renderLanding(app, false); return; }
  return await renderHome(app, params);
});
route('/pricing', marketing(renderPricing));
route('/schools', marketing(renderSchools));
route('/about', marketing(renderAbout));
route('/contact', marketing(renderContact));
route('/terms', marketing(renderTerms));
route('/privacy', marketing(renderPrivacy));
route('/refund', marketing(renderRefund));
route('/course/:courseId', guard(renderCourse));
route('/course/:courseId/topic/:topicId', guard(renderStudio));
route('/settings', guard(renderSettings));
route('/marketplace', guard(renderMarketplace));
route('/wallet', guard(renderWallet));
route('/school', guard(renderSchool));
route('/scores', guard(renderScores));
route('/school/student/:email', guard(renderSchoolStudentScores));
route('/school/scores', guard(renderSchoolScores));
route('/teaching', guard(renderTeaching));
// Standalone first-person driving-rules simulator — a real, freestanding
// game page for practicing an Indian LMV license test (indicators, road
// signs, overtaking, stopping), not a course/topic: no Firestore course or
// topic document backs it, nothing here is graded/saved. See
// src/views/drivingTest.js for the hand-authored (not LLM-generated) app.
route('/drive', guard(async (container) => {
  const { renderDrivingTest } = await import('./views/drivingTest.js');
  return renderDrivingTest(container);
}));
// Public, school-branded onboarding (handles its own sign-in/sign-up) so a
// school never lands on the generic learner login.
route('/school/start', async () => {
  if (!authReady) { app.innerHTML = `<div class="center-screen">Loading…</div>`; return; }
  return renderSchoolStart(app);
});
route('/dwar', guard(renderAdmin));
route('/dwar/test-area', guard(async (container) => {
  // Keep the experimental bundle out of ordinary user sessions. The view
  // performs its own server-side administrator check before importing it.
  const { renderAdminTestArea } = await import('./views/adminTestArea.js');
  return renderAdminTestArea(container);
}));
route('/dwar/ui-replica', guard(async (container) => {
  // Keep the Three.js-dependent bundle out of ordinary user sessions. The
  // view performs its own server-side administrator check before importing it.
  const { renderUiReplicaStudio } = await import('./views/uiReplicaStudio.js');
  return renderUiReplicaStudio(container);
}));
route('/dwar/agent-activity', guard(async (container) => {
  // Same "keep it out of ordinary sessions, view does its own admin check"
  // pattern as the two routes above. See src/views/adminAgentActivity.js.
  const { renderAdminAgentActivity } = await import('./views/adminAgentActivity.js');
  return renderAdminAgentActivity(container);
}));
route('/dwar/kinematics-lab', guard(async (container) => {
  // Same "keep it out of ordinary sessions, view does its own admin check"
  // pattern as the routes above. See src/views/adminKinematicsLab.js — a
  // hand-authored (not LLM-generated) ungraded empirical kinematics
  // sandbox, not real course generation.
  const { renderAdminKinematicsLab } = await import('./views/adminKinematicsLab.js');
  return renderAdminKinematicsLab(container);
}));
route('/dwar/collider-lab', guard(async (container) => {
  // Same "keep it out of ordinary sessions, view does its own admin check"
  // pattern as the routes above. See src/views/adminColliderLab.js — a
  // hand-authored (not LLM-generated) ungraded LHC collision/detector-data
  // sandbox, not real course generation.
  const { renderAdminColliderLab } = await import('./views/adminColliderLab.js');
  return renderAdminColliderLab(container);
}));

// Shown when this tab gets signed out because the account logged in
// elsewhere (see lib/session.js "one account, one login at a time").
function showForcedLogoutToast() {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = '🔒 You were signed out because this account signed in on another device.';
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 10000);
  navigate('/login');
}

watchAuth((user) => {
  authReady = true;
  const emailVerificationRequired = requiresEmailVerification(user);
  signedIn = !!user && !emailVerificationRequired;
  if (signedIn) {
    // Persistent sessions (reloads) never run the explicit sign-in path, so
    // refresh server-reported account flags (e.g. school ownership) here —
    // fire-and-forget, never blocks rendering.
    refreshAccountFlags();
    // Single-session enforcement: watch this account's one session doc for
    // as long as this tab stays signed in (reload or fresh sign-in alike —
    // see lib/session.js for why only an EXPLICIT sign-in mints a new id).
    startSessionGuard(user.uid, { onForcedLogout: showForcedLogoutToast });
  } else {
    stopSessionGuard();
  }
  const hash = (location.hash.slice(1) || '/').split('?')[0];
  if (emailVerificationRequired && hash !== '/verify-email') {
    navigate('/verify-email');
  } else if (!signedIn && !PUBLIC_PATHS.has(hash)) {
    navigate('/login');
  } else if (signedIn && hash === '/login') {
    navigate('/');
  } else {
    // re-resolve current route now that auth state is known
    window.dispatchEvent(new HashChangeEvent('hashchange'));
  }
});

startRouter();
